import { useEffect, useId, useState } from 'react';
import {
  // standards: allow file-length because CapturePoint port, split tracked in docs/engineering/workbench-port.md
  ArrowLeft,
  Check,
  ChevronLeft,
  ChevronRight,
  FileSearch,
  Files,
  LayoutGrid,
  LoaderCircle,
  MoreHorizontal,
  Plus,
  ScanText,
  ShieldCheck,
  Sparkles,
  X,
} from 'lucide-react';
import { SUGGESTION, SUGGESTION_ACTION, type JobSuggestion } from '../shared/desktop-api';
import { LazyPageThumbnail } from './LazyPageThumbnail';
import { TooltipButton } from './TooltipButton';
import { SupportingPage, type SuggestionSupportingDocument } from './SuggestionSupportingPage';
import {
  CUSTOM_TYPE_NAME,
  suggestionApplyIntent,
  suggestionGroupSelection,
  suggestionTypeName,
  type SuggestionApplyIntent,
  type SuggestionDocketChoices,
} from './suggestion-docket';
import { suggestionCountNoun } from './learning-stance';
import { keptSuggestedFieldNames, suggestedFieldsStatus } from './suggested-type-fields';

export type SuggestionDecision = 'not-now' | 'snooze' | 'never';
export type SuggestionScope = 'refresh-safe' | 'future-only';

/**
 * One job adjustment, reviewed where the operator already is. The decision
 * rules are the drawer's own (suggestion-docket): the same name choice and the
 * same document exclusions reach the native apply. Applying is handed to the
 * document workspace because it owns the batch lock, re-evaluation and Undo.
 */
export function LearningSuggestionReview({
  suggestion,
  index,
  total,
  documentTypes,
  documents,
  documentsState,
  busy,
  canApply,
  autopilot = false,
  onNavigate,
  onBack,
  onApply,
  onDecide,
}: {
  suggestion: JobSuggestion;
  index: number;
  total: number;
  documentTypes: readonly { id: string; name: string }[];
  documents: readonly SuggestionSupportingDocument[];
  documentsState: 'loading' | 'ready' | 'error';
  busy: boolean;
  canApply: boolean;
  /** The job runs on Autopilot: a new type with no fields sends its documents out without values. */
  autopilot?: boolean;
  onNavigate: (delta: -1 | 1) => void;
  onBack: () => void;
  onApply: (intent: SuggestionApplyIntent, scope: SuggestionScope) => void;
  onDecide: (decision: SuggestionDecision) => void;
}) {
  const id = useId();
  const typeGroup = suggestion.kind === SUGGESTION.documentType;
  const proposed = suggestion.proposedLabel ?? '';
  const proposedExisting = documentTypes.find((type) => type.name === proposed)?.name;
  const [choices, setChoices] = useState<SuggestionDocketChoices>({
    names: {},
    customNames: {},
    excluded: {},
  });
  const [mode, setMode] = useState<'new' | 'existing'>(proposedExisting ? 'existing' : 'new');
  const [typed, setTyped] = useState(proposed);
  const [existing, setExisting] = useState(proposedExisting ?? documentTypes[0]?.name ?? '');
  const [big, setBig] = useState<number | null>(null);
  const [page, setPage] = useState(0);
  const [moreOpen, setMoreOpen] = useState(false);
  // The proposed fields the operator dropped (D10); every other one is kept.
  const [droppedFields, setDroppedFields] = useState<string[]>([]);
  useEffect(() => {
    setMode(proposedExisting ? 'existing' : 'new');
    setTyped(proposed);
    setExisting(proposedExisting ?? documentTypes[0]?.name ?? '');
    setBig(null);
    setPage(0);
    setMoreOpen(false);
    setDroppedFields([]);
  }, [suggestion.id]);

  // Resolve the radio choice into the drawer's choice shape, so apply intent is computed one way only.
  const names = { ...choices.names },
    customNames = { ...choices.customNames };
  if (typeGroup) {
    if (mode === 'existing') names[suggestion.id] = existing;
    else if (typed.trim() === proposed && proposed) names[suggestion.id] = proposed;
    else {
      names[suggestion.id] = CUSTOM_TYPE_NAME;
      customNames[suggestion.id] = typed;
    }
  }
  const resolved = { ...choices, names, customNames };
  const intent = suggestionApplyIntent(suggestion.id, resolved);
  const excluded = resolved.excluded[suggestion.id] ?? [];
  const typeName = typeGroup
    ? suggestionTypeName(mode === 'existing' ? existing : typed, documentTypes)
    : null;
  const blocked = intent.blocked || (typeGroup && !typeName?.name);
  // Adding a type: the fields designed for it from these documents, kept or dropped here before Apply (D10).
  const addsType = typeGroup && Boolean(typeName?.name) && !typeName?.existing;
  const proposedFields = suggestion.proposedFields;
  const keptFields = addsType ? keptSuggestedFieldNames(proposedFields, droppedFields) : [];
  const toggleField = (name: string) =>
    setDroppedFields((current) =>
      current.includes(name) ? current.filter((item) => item !== name) : [...current, name],
    );
  const applyIntent: SuggestionApplyIntent = addsType
    ? { ...intent, keptFieldNames: keptFields }
    : intent;
  const selection = suggestionGroupSelection(suggestion, excluded);
  const evidenceIds = suggestion.evidenceDocumentIds ?? [];
  const supporting = evidenceIds
    .map((documentId) => documents.find((document) => document.id === documentId))
    .filter((document): document is SuggestionSupportingDocument => Boolean(document));
  const toggle = (documentId: string) =>
    setChoices((current) => {
      const list = current.excluded[suggestion.id] ?? [];
      return {
        ...current,
        excluded: {
          ...current.excluded,
          [suggestion.id]: list.includes(documentId)
            ? list.filter((item) => item !== documentId)
            : [...list, documentId],
        },
      };
    });
  const action =
    SUGGESTION_ACTION[suggestion.kind as keyof typeof SUGGESTION_ACTION] ?? 'Improve this job';
  const count =
    Number.isSafeInteger(suggestion.supportCount) && suggestion.supportCount >= 0
      ? suggestion.supportCount
      : 0;
  const shown = big === null ? null : supporting[big];
  const shownPage = shown?.pages[Math.min(page, Math.max(0, shown.pages.length - 1))];
  const step = (delta: number) => {
    if (!supporting.length) return;
    setBig((current) => ((current ?? 0) + delta + supporting.length) % supporting.length);
    setPage(0);
  };
  const apply = (scope: SuggestionScope) => {
    if (!blocked && !busy && canApply) {
      setMoreOpen(false);
      onApply(applyIntent, scope);
    }
  };
  const decide = (decision: SuggestionDecision) => {
    if (!busy) {
      setMoreOpen(false);
      onDecide(decision);
    }
  };

  return (
    <section className="lj-review" aria-labelledby={`${id}-title`}>
      <header className="lj-review-top">
        <button type="button" className="quiet-button small" onClick={onBack}>
          <ArrowLeft size={15} />
          Learning
        </button>
        <span className="lj-review-crumb">
          <Sparkles size={15} aria-hidden="true" />
          Suggestions{' '}
          <b>
            {index + 1} of {total}
          </b>
        </span>
        <span className="lj-grow" />
        <nav className="lj-review-nav" aria-label="Suggestion navigation">
          <button
            type="button"
            className="icon-button"
            disabled={index <= 0}
            onClick={() => onNavigate(-1)}
            aria-label="Previous suggestion"
          >
            <ChevronLeft size={18} />
          </button>
          <button
            type="button"
            className="icon-button"
            disabled={index >= total - 1}
            onClick={() => onNavigate(1)}
            aria-label="Next suggestion"
          >
            <ChevronRight size={18} />
          </button>
        </nav>
      </header>
      <div className="lj-review-body">
        <section className="lj-review-decision" aria-label="Current suggestion">
          <div>
            <span className="lj-review-kind">
              {typeGroup ? (
                <Files size={14} aria-hidden="true" />
              ) : (
                <ScanText size={14} aria-hidden="true" />
              )}
              {typeGroup ? 'New document type' : action}
            </span>
            <h3 id={`${id}-title`}>
              {typeGroup
                ? `What are these ${count.toLocaleString()} documents?`
                : suggestion.proposedLabel || suggestion.title}
            </h3>
            <p>
              {typeGroup
                ? 'They don’t match a type in this job.'
                : suggestion.proposedLabel
                  ? suggestion.title
                  : `${count.toLocaleString()} ${suggestionCountNoun(suggestion.kind)}${count === 1 ? '' : 's'}`}
            </p>
          </div>
          {typeGroup && (
            <div
              className="lj-review-options"
              role="radiogroup"
              aria-label="What are these documents?"
            >
              <label className={`lj-review-option ${mode === 'new' ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name={`${id}-mode`}
                  checked={mode === 'new'}
                  onChange={() => setMode('new')}
                  disabled={busy}
                />
                <span>
                  <b>A new type</b>
                  <input
                    className="lj-review-input"
                    value={typed}
                    disabled={busy}
                    aria-label="New type name"
                    onFocus={() => setMode('new')}
                    onChange={(event) => {
                      setMode('new');
                      setTyped(event.target.value);
                    }}
                  />
                </span>
              </label>
              <label className={`lj-review-option ${mode === 'existing' ? 'is-checked' : ''}`}>
                <input
                  type="radio"
                  name={`${id}-mode`}
                  checked={mode === 'existing'}
                  onChange={() => setMode('existing')}
                  disabled={busy || !documentTypes.length}
                />
                <span>
                  <b>A type this job already has</b>
                  <select
                    className="lj-review-input"
                    value={existing}
                    disabled={busy || !documentTypes.length}
                    aria-label="Existing type"
                    onFocus={() => setMode('existing')}
                    onChange={(event) => {
                      setMode('existing');
                      setExisting(event.target.value);
                    }}
                  >
                    {documentTypes.map((type) => (
                      <option key={type.id} value={type.name}>
                        {type.name}
                      </option>
                    ))}
                  </select>
                </span>
              </label>
            </div>
          )}
          {addsType && (
            <fieldset className="lj-review-fields" disabled={busy}>
              <legend>Fields it will read</legend>
              {proposedFields?.length ? (
                <div className="lj-review-field-list">
                  {proposedFields.map((field) => {
                    const kept = !droppedFields.includes(field.name);
                    return (
                      <button
                        key={field.name}
                        type="button"
                        className={`lj-review-field ${kept ? 'is-kept' : ''}`}
                        aria-pressed={kept}
                        title={field.description ?? undefined}
                        onClick={() => toggleField(field.name)}
                      >
                        {kept ? (
                          <Check size={13} aria-hidden="true" />
                        ) : (
                          <Plus size={13} aria-hidden="true" />
                        )}
                        <span>{field.name}</span>
                      </button>
                    );
                  })}
                </div>
              ) : null}
              <small role="status">
                {suggestedFieldsStatus(proposedFields, keptFields.length, autopilot)}
              </small>
            </fieldset>
          )}
          {suggestion.decisionDetails?.length ? (
            <dl className="lj-review-details">
              {suggestion.decisionDetails.map((item, itemIndex) => (
                <div key={itemIndex}>
                  <dt>{item.label}</dt>
                  <dd>{item.value}</dd>
                </div>
              ))}
            </dl>
          ) : null}
          <div className="lj-review-what">
            {typeGroup && (
              <div>
                <Files size={16} aria-hidden="true" />
                <span>
                  <b>
                    {selection.included !== null
                      ? `${selection.included.toLocaleString()} matching ${selection.included === 1 ? 'document' : 'documents'}`
                      : 'The matching documents'}
                  </b>
                  {typeName?.name ? (
                    typeName.existing ? (
                      <>
                        {' '}
                        are filed as <b>{typeName.name}</b>.
                      </>
                    ) : (
                      <>
                        {' '}
                        go into the new type <b>{typeName.name}</b>.
                      </>
                    )
                  ) : (
                    ' need a type name first.'
                  )}{' '}
                  {keptFields.length
                    ? 'Confirmed types stay, as do documents with confirmed values these fields can’t hold.'
                    : 'Confirmed document types stay unchanged.'}
                </span>
              </div>
            )}
            <div>
              <Check size={16} aria-hidden="true" />
              <span>
                Apply opens this job’s documents, makes the change there and refreshes them. Undo
                stays available.
              </span>
            </div>
            <div>
              <ShieldCheck size={16} aria-hidden="true" />
              <span>Nothing changes until you apply.</span>
            </div>
          </div>
          {suggestion.detail?.trim() && (
            <details className="lj-advanced">
              <summary>
                <ChevronRight size={15} aria-hidden="true" />
                Why this suggestion
              </summary>
              <div className="lj-advanced-body">
                {suggestion.detail
                  .split('\n')
                  .filter(Boolean)
                  .map((line, lineIndex) => (
                    <p key={lineIndex}>{line}</p>
                  ))}
              </div>
            </details>
          )}
          <div className="lj-review-actions">
            <button
              type="button"
              className="primary-button"
              disabled={busy || blocked || !canApply}
              onClick={() => apply('refresh-safe')}
            >
              {busy ? <LoaderCircle size={16} className="spin" /> : <Check size={16} />}
              <span>
                {typeGroup
                  ? typeName?.name
                    ? typeName.existing
                      ? `File as ${typeName.name}`
                      : `Add ${typeName.name}`
                    : 'Choose a type'
                  : 'Apply'}
              </span>
            </button>
            <button
              type="button"
              className="quiet-button"
              disabled={busy}
              onClick={() => decide('not-now')}
            >
              Not now
            </button>
            <button
              type="button"
              className="quiet-button"
              disabled={busy}
              onClick={() => decide('never')}
            >
              Don't suggest again
            </button>
            <span className="lj-review-more">
              <TooltipButton
                type="button"
                className="quiet-button"
                disabled={busy}
                aria-expanded={moreOpen}
                aria-label="More suggestion options"
                tooltip="More"
                onClick={() => setMoreOpen((value) => !value)}
              >
                <MoreHorizontal size={18} />
              </TooltipButton>
              {moreOpen && (
                <span className="lj-review-menu" role="group" aria-label="More suggestion options">
                  {!typeGroup && (
                    <button
                      type="button"
                      disabled={busy || blocked || !canApply}
                      onClick={() => apply('future-only')}
                    >
                      Use for new pages only
                    </button>
                  )}
                  <button type="button" disabled={busy} onClick={() => decide('snooze')}>
                    Don’t suggest today
                  </button>
                </span>
              )}
            </span>
          </div>
          {!canApply && <p className="lj-quiet">Apply is available from this job’s documents.</p>}
        </section>

        <section className="lj-review-evidence" aria-label="Supporting documents">
          {shown ? (
            <>
              <div className="lj-evidence-head">
                <button type="button" className="lj-evidence-button" onClick={() => setBig(null)}>
                  <LayoutGrid size={15} />
                  All {supporting.length}
                </button>
                <span>
                  <b>{shown.sourceName}</b> · {big! + 1} of {supporting.length}
                </span>
                <button
                  type="button"
                  className={`lj-evidence-button ${excluded.includes(shown.id) ? 'is-on' : ''}`}
                  disabled={busy}
                  aria-pressed={excluded.includes(shown.id)}
                  onClick={() => toggle(shown.id)}
                >
                  {excluded.includes(shown.id) ? (
                    <>
                      <Plus size={15} />
                      Include it
                    </>
                  ) : (
                    <>
                      <X size={15} />
                      Leave this one out
                    </>
                  )}
                </button>
              </div>
              <div className="lj-evidence-large">
                <button
                  type="button"
                  className="lj-evidence-arrow"
                  onClick={() => step(-1)}
                  aria-label="Previous supporting document"
                >
                  <ChevronLeft size={20} />
                </button>
                <div
                  className={`lj-evidence-page ${excluded.includes(shown.id) ? 'is-excluded' : ''}`}
                >
                  {shownPage ? (
                    <SupportingPage
                      key={`${shown.id}:${shownPage.number}`}
                      document={shown}
                      page={shownPage}
                    />
                  ) : (
                    <div className="lj-evidence-empty">
                      <FileSearch size={28} />
                      <span>Page preview unavailable</span>
                    </div>
                  )}
                  {shown.pages.length > 1 && (
                    <div className="lj-evidence-pages">
                      <button
                        type="button"
                        className="icon-button"
                        disabled={page <= 0}
                        onClick={() => setPage((value) => value - 1)}
                        aria-label="Previous page"
                      >
                        <ChevronLeft size={16} />
                      </button>
                      <span>
                        Page {page + 1} of {shown.pages.length}
                      </span>
                      <button
                        type="button"
                        className="icon-button"
                        disabled={page >= shown.pages.length - 1}
                        onClick={() => setPage((value) => value + 1)}
                        aria-label="Next page"
                      >
                        <ChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
                <button
                  type="button"
                  className="lj-evidence-arrow"
                  onClick={() => step(1)}
                  aria-label="Next supporting document"
                >
                  <ChevronRight size={20} />
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="lj-evidence-head">
                <span>
                  <b>
                    {(
                      supporting.length -
                      excluded.filter((item) => supporting.some((document) => document.id === item))
                        .length
                    ).toLocaleString()}{' '}
                    of {supporting.length.toLocaleString()}
                  </b>{' '}
                  supporting documents included
                </span>
                {supporting.length > 0 && (
                  <small>Click a page to look closer · click ✓ to leave one out</small>
                )}
              </div>
              {documentsState === 'loading' ? (
                <div className="lj-evidence-empty" role="status">
                  <LoaderCircle size={22} className="spin" />
                  <span>Loading supporting documents…</span>
                </div>
              ) : !supporting.length ? (
                <div className="lj-evidence-empty">
                  <FileSearch size={28} />
                  <span>
                    {documentsState === 'error'
                      ? 'Supporting documents couldn’t be read.'
                      : 'Supporting pages are not available in the current documents.'}
                  </span>
                </div>
              ) : (
                <div className="lj-evidence-grid">
                  {supporting.map((document, documentIndex) => {
                    const out = excluded.includes(document.id);
                    return (
                      <div
                        key={document.id}
                        className={`lj-evidence-thumb ${out ? 'is-excluded' : ''}`}
                      >
                        <button
                          type="button"
                          className="lj-evidence-open"
                          onClick={() => {
                            setBig(documentIndex);
                            setPage(0);
                          }}
                          aria-label={`Look at ${document.sourceName}`}
                        >
                          {document.pages[0] ? (
                            <LazyPageThumbnail
                              previewPath={document.pages[0].previewPath}
                              label={document.sourceName}
                            />
                          ) : (
                            <FileSearch size={20} />
                          )}
                        </button>
                        <TooltipButton
                          type="button"
                          className="lj-evidence-check"
                          disabled={busy}
                          aria-pressed={!out}
                          onClick={() => toggle(document.id)}
                          aria-label={
                            out
                              ? `Include ${document.sourceName}`
                              : `Leave out ${document.sourceName}`
                          }
                          tooltip={out ? 'Include' : 'Leave out'}
                        >
                          {out ? <Plus size={13} /> : <Check size={13} />}
                        </TooltipButton>
                        <small>{document.sourceName}</small>
                      </div>
                    );
                  })}
                </div>
              )}
            </>
          )}
          <p className="lj-evidence-foot">
            These pages support the suggestion; no exact region is identified.
            {supporting.length < evidenceIds.length && documentsState === 'ready'
              ? ` ${supporting.length} of ${evidenceIds.length} supporting documents are in the current documents.`
              : ''}
          </p>
        </section>
      </div>
    </section>
  );
}
